import { AxiosError } from 'axios';
import { bffApi } from '../../../services/bff/bffApi';
import { BaseService } from '../../../global/classes';
import {
  IReprocessTransactionRequest,
  IReprocessTransactionResponse,
  IReprocessPixRequest,
  IReprocessPixAccepted,
  IPixJobStatus,
  JobType,
} from './types';

export interface IReprocessPixOutcome {
  data: IReprocessPixAccepted;
  alreadyRunning: boolean;
}

/**
 * Endpoint de consulta de status por tipo de job. Há apenas dois:
 *  - PIX  → v1/pix/reprocess/status
 *  - Demais (crédito/débito, tanto Phoebus quanto Aditum) → v1/transaction/reprocess/status
 * Ambos retornam o mesmo shape (IPixJobStatus).
 */
const STATUS_PATH: Record<JobType, string> = {
  [JobType.Pix]: 'v1/pix/reprocess/status',
  [JobType.Transaction]: 'v1/transaction/reprocess/status',
};

class ReprocessamentoService extends BaseService {
  reprocessarTransacao = async (
    payload: IReprocessTransactionRequest,
  ): Promise<IReprocessTransactionResponse> => {
    const result = await bffApi.post<IReprocessTransactionResponse>(
      `/api/kaptor/v1/transaction/reprocess`,
      payload,
    );
    return result.data;
  };

  reprocessarPix = async (
    payload: IReprocessPixRequest,
  ): Promise<IReprocessPixOutcome> => {
    try {
      const result = await bffApi.post<IReprocessPixAccepted>(
        `/api/kaptor/v1/pix/reprocess`,
        payload,
      );
      return { data: result.data, alreadyRunning: false };
    } catch (error) {
      const axiosError = error as AxiosError<IReprocessPixAccepted>;
      if (axiosError.response?.status === 409 && axiosError.response.data?.jobId) {
        return { data: axiosError.response.data, alreadyRunning: true };
      }
      throw error;
    }
  };

  /**
   * Consulta de status roteada por tipo de job. Cada método de captura tem
   * seu próprio endpoint (PIX, transação crédito/débito, Phoebus), mas todos
   * são normalizados para o mesmo contrato IPixJobStatus, de modo que a UI
   * de consulta não precisa saber a diferença.
   */
  consultarStatus = async (
    jobType: JobType,
    jobId: string,
  ): Promise<IPixJobStatus> => {
    const response = await bffApi.get<IPixJobStatus>(
      `/api/kaptor/${STATUS_PATH[jobType]}/${encodeURIComponent(jobId)}`,
    );

    const data = response.data;

    // O backend de transação aninha os totais em `result` e não devolve
    // `processedCount` na raiz (diferente do PIX). Normalizamos para o mesmo
    // contrato: processedCount = quantidade sincronizada com sucesso (result.sent),
    // de modo que a UI de consulta funcione igual para os dois fluxos.
    if (data.processedCount == null && data.result) {
      return { ...data, processedCount: data.result.sent };
    }

    return data;
  };

  /** @deprecated Use consultarStatus(JobType.Pix, jobId). Mantido por compat. */
  consultarStatusPix = async (jobId: string): Promise<IPixJobStatus> =>
    this.consultarStatus(JobType.Pix, jobId);
}

const reprocessamentoService = new ReprocessamentoService();

export default reprocessamentoService;
